import React, { useState } from "react";
import { Plus, RefreshCw, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useInfluencerRefresh } from "@/hooks/useInfluencerRefresh";
import { ExportButton } from "@/shared/ui/export/ExportButton";
import { AssignedInfluencersTable } from "./AssignedInfluencersTable";
import { ExportCampaignInfluencersModal } from "./ExportCampaignInfluencersModal";
import type { CampaignInfluencer } from "../types";

interface InfluencersTabContentProps {
  campaignId?: string;
  influencers: CampaignInfluencer[];
  onAddInfluencer: () => void;
  onViewProfile: (influencerId: string) => void;
  onStatusChange: (influencerId: string, newStatus: string) => void;
  isUpdatingStatus: boolean;
  onEdit: (influencer: CampaignInfluencer) => void;
  onDelete: (influencer: CampaignInfluencer) => void;
}

export const InfluencersTabContent: React.FC<InfluencersTabContentProps> = ({
  campaignId,
  influencers,
  onAddInfluencer,
  onViewProfile,
  onStatusChange,
  isUpdatingStatus,
  onEdit,
  onDelete,
}) => {
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const {
    isPolling,
    isEnqueueing,
    canRefreshSelected,
    selectedCount: refreshCount,
    maxSelectedRefreshes,
    refreshProgress,
    handleSelectedRefresh,
  } = useInfluencerRefresh();

  const handleRefreshAllMetrics = async () => {
    const influencerIds = influencers.map((inf) => inf.id).filter(Boolean);
    if (influencerIds.length === 0) return;

    const success = await handleSelectedRefresh(influencerIds);
    if (campaignId) {
      queryClient.invalidateQueries({
        queryKey: ["campaign-influencers", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaign-with-contact", campaignId],
      });
    }
    queryClient.invalidateQueries({
      queryKey: ["campaign-influencers"],
    });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-[20px] font-bold font-['Hanken_Grotesk',sans-serif]">
          Assigned Influencers ({influencers.length})
        </h3>
        <div className="flex items-center gap-3">
          {isPolling ? (
            <Button
              variant="outline"
              disabled
              title="Refresh in progress…"
              className="flex items-center gap-2"
            >
              <Loader2 className="w-4 h-4 animate-spin text-[#A655F8]" />
              <span>
                {refreshProgress
                  ? `Refreshing ${refreshProgress.processedCount}/${refreshProgress.totalCount}…`
                  : "Refreshing…"}
              </span>
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  disabled={
                    influencers.length === 0 ||
                    isEnqueueing ||
                    !canRefreshSelected
                  }
                  title={
                    influencers.length === 0
                      ? "No influencers to refresh"
                      : canRefreshSelected
                      ? `Refresh metrics for all campaign influencers (${maxSelectedRefreshes - refreshCount} remaining today)`
                      : `You can only refresh influencer data ${maxSelectedRefreshes} times a day`
                  }
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Metrics
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Refresh metrics for {influencers.length} influencer{influencers.length === 1 ? "" : "s"}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This will refresh profile metrics (followers, engagement rate, average views) for all influencers in this campaign. This counts as one of your {maxSelectedRefreshes} daily refreshes.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleRefreshAllMetrics}
                    className="bg-[#A655F8] hover:bg-[#9333ea] text-white"
                  >
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <ExportButton
            variant="purple"
            iconType="spreadsheet"
            onClick={() => setExportModalOpen(true)}
            disabled={influencers.length === 0 || !campaignId}
          />
          <Button
            onClick={onAddInfluencer}
            className="flex items-center gap-2 bg-[#A655F8] text-white rounded-lg"
          >
            <Plus className="w-4 h-4" />
            Add Influencer
          </Button>
        </div>
      </div>

      {influencers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="mb-4">No influencers assigned to this campaign yet</div>
          <Button onClick={onAddInfluencer} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add First Influencer
          </Button>
        </div>
      ) : (
        <AssignedInfluencersTable
          influencers={influencers}
          onViewProfile={onViewProfile}
          onStatusChange={onStatusChange}
          isUpdatingStatus={isUpdatingStatus}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}

      {campaignId && (
        <ExportCampaignInfluencersModal
          open={exportModalOpen}
          onOpenChange={setExportModalOpen}
          campaignId={campaignId}
          totalCount={influencers.length}
        />
      )}
    </>
  );
};

